/**
 * Modal — accessible overlay dialog.
 *
 * Features:
 * - Escape key closes
 * - Focus trap (Tab/Shift+Tab cycle within modal)
 * - aria-modal + role="dialog"
 * - 150ms fade + slide-up animation
 * - Cream-paper backdrop with blur
 * - Header (serif italic title), body slot, footer slot
 *
 * Usage:
 *   <Modal open={open} onClose={() => setOpen(false)} title="Confirm action">
 *     <p>Body content</p>
 *     <Modal.Footer>
 *       <button onClick={() => setOpen(false)}>Cancel</button>
 *       <button onClick={handleConfirm}>Confirm</button>
 *     </Modal.Footer>
 *   </Modal>
 */

import {
  useEffect,
  useId,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

// ── Focusable selectors ──────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

// ── Modal ────────────────────────────────────────────────────────────────────

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** 'sm' = 480px, 'md' = 560px (default), 'lg' = 720px, 'xl' = 960px.
   *  'xl' exists for the two-pane composers (form on one side, a live document
   *  preview on the other — the resolution create flow, Beno 2026-08-22): at
   *  'lg' each pane is under 340px and the preview stops reading as a document.
   *  It is opt-in; every existing consumer keeps the size it asked for. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Extra border colour for danger-zone modals (e.g. var(--negative)) */
  accentBorder?: string;
  /** Backdrop z-index; the dialog renders at zIndex + 1. Default 8000.
   *  Raise it for modals that must stack OVER an already-open modal
   *  (e.g. the global step-up overlay at 8200 over a wizard at 8000). */
  zIndex?: number;
  /** Whether a click on the backdrop closes the modal. Default FALSE since
   *  2026-08-22: a stray click beside a half-filled form silently discarded
   *  the user's input (found live on the invite-staff modal). Escape, the ×
   *  button, and explicit Cancel remain the dismissal paths; read-only modals
   *  that want click-away can opt back in. */
  dismissOnBackdrop?: boolean;
  children: ReactNode;
}

const SIZE_MAP = { sm: 480, md: 560, lg: 720, xl: 960 } as const;

/**
 * Modal-over-modal bookkeeping (FF-UI-2: the step-up overlay opens over an
 * in-flight wizard).
 * - `modalStack` makes Escape close ONLY the top-most open modal: every open
 *   modal registers a document keydown listener, so without the stack one
 *   Escape press would close all of them (the wizard underneath included —
 *   destroying the state the step-up flow exists to preserve).
 * - `scrollLockCount` ref-counts the body overflow lock so closing the top
 *   modal doesn't restore page scroll while another modal is still open.
 */
const modalStack: symbol[] = [];
let scrollLockCount = 0;

export function Modal({ open, onClose, title, size = 'md', accentBorder, zIndex = 8000, dismissOnBackdrop = false, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const stackId = useRef<symbol | null>(null);
  if (stackId.current === null) stackId.current = Symbol('modal');
  // Unique per instance — with stacked modals (wizard + step-up overlay) a
  // shared hardcoded id would make BOTH dialogs' aria-labelledby resolve to
  // the first title in the document.
  const titleId = useId();

  // ── Save + restore focus ───────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      previouslyFocused.current = document.activeElement;
      // Focus first focusable child after paint.
      requestAnimationFrame(() => {
        const first = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
        first?.focus();
      });
    } else {
      (previouslyFocused.current as HTMLElement | null)?.focus?.();
    }
  }, [open]);

  // ── Escape key (top-most open modal only) ──────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const id = stackId.current as symbol;
    modalStack.push(id);
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === id) onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  // ── Body scroll lock (ref-counted across stacked modals) ──────────────────
  useEffect(() => {
    if (!open) return;
    scrollLockCount += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      scrollLockCount -= 1;
      if (scrollLockCount <= 0) {
        scrollLockCount = 0;
        document.body.style.overflow = '';
      }
    };
  }, [open]);

  // ── Focus trap ─────────────────────────────────────────────────────────────
  const handleTab = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
    ).filter((el) => !el.closest('[aria-hidden="true"]'));
    if (focusables.length === 0) { e.preventDefault(); return; }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, []);

  if (!open) return null;

  const maxW = SIZE_MAP[size];

  return createPortal(
    <>
      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: translateY(14px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      {/* Backdrop.
          `modal-backdrop` is a PRINT HOOK, not a style hook — every visual is
          still inline below. `styles/design-system.css`'s `@media print` block
          has listed `.modal-backdrop` in its hide-list since it was written,
          but nothing ever rendered the class, so the rule was dead: printing
          with a modal open laid a translucent black sheet over the page. */}
      <div
        className="modal-backdrop"
        aria-hidden="true"
        onClick={dismissOnBackdrop ? onClose : undefined}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,18,12,0.45)',
          backdropFilter: 'blur(4px)',
          zIndex,
          animation: 'backdrop-in 150ms var(--ease-cushion)',
        }}
      />

      {/* Dialog.
          `modal-dialog` / `modal-card` / `modal-header` / `modal-body` (and
          `modal-footer` below) are inert on screen — no stylesheet paints them
          and every visual stays inline. They exist so a modal that hosts a
          printable `.print-area` (the resolution draft preview) can flatten the
          fixed, viewport-capped shell for paper WITHOUT that stylesheet needing
          to know the shell's DOM shape. See
          `routes/app/trusts/$id/resolutions/resolution-draft-preview.css`. */}
      <div
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        onKeyDown={handleTab}
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndex + 1,
          padding: '24px 16px',
          pointerEvents: 'none',
        }}
      >
        <div
          className="modal-card"
          style={{
            width: '100%',
            maxWidth: maxW,
            // Cap to the viewport and let the BODY scroll. Body scroll is locked
            // while a modal is open, so a card taller than the screen used to
            // clip its lower fields with no way to reach them (found live on the
            // add-beneficial-owner modal, 2026-08-22 walkthrough).
            maxHeight: 'calc(100dvh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
            border: `1px solid ${accentBorder ?? 'var(--hairline)'}`,
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 8px 48px -8px rgba(20,18,12,0.24), 0 1px 0 rgba(255,255,255,0.6) inset',
            animation: 'modal-in 150ms var(--ease-cushion)',
            pointerEvents: 'auto',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            className="modal-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '24px 28px 20px',
              borderBottom: '1px solid var(--hairline)',
              flexShrink: 0,
            }}
          >
            <h2
              id={titleId}
              className="serif-heading"
              style={{ margin: 0, fontWeight: 300, fontSize: 24, lineHeight: 1.2 }}
            >
              {title}
            </h2>
            <button
              aria-label="Close"
              onClick={onClose}
              style={{
                width: 32,
                height: 32,
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--ink-3)',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 1l11 11M12 1L1 12" />
              </svg>
            </button>
          </div>

          {/* Body — the scroll container when content exceeds the viewport cap */}
          <div className="modal-body" style={{ padding: '22px 28px 0', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// ── Modal.Footer ─────────────────────────────────────────────────────────────

function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div
      className="modal-footer"
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        padding: '20px 28px 24px',
        marginTop: 20,
      }}
    >
      {children}
    </div>
  );
}

Modal.Footer = ModalFooter;
